import { useEffect, useRef } from 'react';
import styled from '@emotion/styled';

import { tokens } from '@hotjar/hotsauce';
import { sendHighlightEvent } from '@hotjar/webapp/domains/highlights/api';
import { HighlightsEventType } from '@hotjar/webapp/domains/highlights/dto';
import { CommentsContext } from '@hotjar/webapp/contexts/comments';

import { Collections } from './Collections';
import { CommentsThread } from './CommentsThread';
import type { CommentsThreadProps } from './CommentsThread';
import { Details } from './Details';
import { FullItemLink } from './FullItemLink';
import type { HeatmapImageRef } from '../Heatmaps/HeatmapImage';
import { Header } from './Header';
import { HighlightContent } from './HighlightContent';
import { HighlightError } from './HighlightError';
import { Labels } from './Labels';
import { useHighlight } from './use-highlight';
import { useViewTracking } from './use-view-tracking';

const Wrapper = styled.section`
  display: flex;
  height: 100%;
  width: 100%;
`;

const Content = styled.div`
  background: ${tokens.color.neutral_500};
  flex-grow: 1;
  overflow: hidden;
  position: relative;
`;

const FullSizeHighlightContent = styled(HighlightContent)`
  height: 100%;
  width: 100%;
`;

const StyledFullItemLink = styled(FullItemLink)`
  left: ${tokens.spacer.x1};
  position: absolute;
  top: ${tokens.spacer.x1};
`;

const Sidebar = styled.nav`
  background-color: ${tokens.color.neutral_100};

  flex-grow: 0;
  flex-shrink: 0;

  display: flex;
  flex-direction: column;
  width: 292px;
`;

const ScrollableWrapper = styled.div`
  background-color: inherit;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  flex: 1;
`;

export interface SingleHighlightProps {
  currentUser: (CommentsThreadProps['currentUser'] & { id: number }) | undefined;
  highlightId: string;
  organizationId: CommentsThreadProps['organizationId'];
  onClose: () => void;
  siteId: string;
}

export function SingleHighlight({
  currentUser,
  highlightId,
  onClose,
  organizationId,
  siteId,
}: SingleHighlightProps): JSX.Element | null {
  const highlight = useHighlight(siteId, highlightId);

  useEffect(() => {
    sendHighlightEvent(
      siteId,
      { highlightId, eventType: HighlightsEventType.HIGHLIGHT_VIEWED },
      { highlightArea: 'all_highlights_area' },
    );
  }, [siteId, highlightId]);

  const svgRef = useRef<HeatmapImageRef>(null);

  useViewTracking(highlight);

  if (highlight.status === 'error') {
    return <HighlightError />;
  }

  return (
    <Wrapper>
      <Content>
        <FullSizeHighlightContent highlight={highlight?.data} siteId={siteId} svgRef={svgRef} />
        <StyledFullItemLink
          highlightType={highlight?.data?.type}
          url={highlight?.data?.fullItemUrl}
        />
      </Content>
      <Sidebar>
        <Header
          authorIsCurrentUser={highlight?.data?.author?.id === String(currentUser?.id)}
          highlight={highlight.data}
          onClose={onClose}
          siteId={siteId}
          svgRef={svgRef}
        />
        <ScrollableWrapper>
          <Collections
            isLoading={!highlight?.data}
            highlightCollections={highlight.data?.collections ?? []}
            highlightId={highlightId}
            highlightType={highlight.data?.type ?? null}
            siteId={siteId}
          />
          <Labels
            isLoading={!highlight?.data}
            highlightLabels={highlight.data?.labels ?? []}
            highlightId={highlightId}
            siteId={siteId}
          />
          {currentUser && (
            <CommentsContext>
              <CommentsThread
                isLoading={!highlight?.data}
                currentUser={currentUser}
                commentId={highlight?.data?.commentId}
                highlight={{
                  id: highlight.data?.id ?? '',
                  createdDate: highlight.data?.createdDate ?? new Date(),
                  autoCreated: highlight.data?.autoCreated ?? false,
                }}
                organizationId={organizationId}
                siteId={siteId}
              />
            </CommentsContext>
          )}
          <Details highlight={highlight.data} />
        </ScrollableWrapper>
      </Sidebar>
    </Wrapper>
  );
}
